import React from 'react';

import { ROUTES } from '@/config/routes';
import {
  Bell,
  Building,
  Building2,
  Calendar,
  ClipboardList,
  CreditCard,
  FileText,
  Flag,
  LayoutDashboard,
  MapPin,
  Trophy,
  User,
  UserCheck,
  UserPlus,
  Users,
  Users2,
  Zap,
} from 'lucide-react';

export type RoleId = 'organizer' | 'club' | 'player';

export interface FeatureDef {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  items: string[];
  badge?: string;
  badgeColor?: string;
}

const ORGANIZER_FEATURES: FeatureDef[] = [
  {
    icon: Calendar,
    title: 'Création de concours',
    badge: 'Complet',
    badgeColor: 'bg-blue-100 text-blue-700',
    items: [
      'Types : Tête-à-tête, Doublette, Triplette',
      'Niveaux : Local à Prestige',
      'Affiche portrait (upload, optimisé WebP)',
      'Géolocalisation avec carte interactive',
      "Prix, limite d'équipes, restriction membres",
      'Règles personnalisables (Informations / Attention / Important)',
    ],
  },
  {
    icon: Users,
    title: 'Gestion des inscriptions',
    items: [
      'Validation / rejet des équipes',
      'Statuts : En attente → Confirmé / Rejeté',
      'Export CSV des participants',
      'Préinscription capitaine connecté',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Saisie des résultats',
    badge: 'Nouveau',
    badgeColor: 'bg-violet-100 text-violet-700',
    items: [
      'Classement final par équipe (rang libre)',
      'Ex-æquos permis (demi-finalistes, quarts…)',
      'Clôture en un clic → podium public',
      'Export CSV du classement final',
    ],
  },
  {
    icon: CreditCard,
    title: 'Stripe Connect Standard',
    items: [
      'Connexion Stripe Standard (OAuth)',
      'Encaissement direct sur votre compte bancaire',
      'Commission Partenaire automatique : 2 % + 0,30 € (min 0,75 €) sur les inscriptions payées',
      'Tableau de bord paiements par concours',
    ],
  },
  {
    icon: Zap,
    title: 'Workflow automatisé',
    badge: 'Automatisé',
    badgeColor: 'bg-amber-100 text-amber-700',
    items: [
      'Clôture automatique des inscriptions à échéance',
      'Rappel 48h si aucun résultat saisi (admin notifié)',
      'Emails transactionnels (confirmation paiement, rejet)',
      'Offre unique Commission Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe',
    ],
  },
  {
    icon: Flag,
    title: 'Clubs & affiliations',
    items: [
      'Gestion des clubs partenaires affiliés',
      'Filtres géographiques (département / région)',
      'Accès exclusif aux membres de clubs affiliés',
      "Suivi des demandes d'affiliation",
    ],
  },
  {
    icon: FileText,
    title: 'Compatibilité fédérale FFPJP',
    badge: 'FFPJP',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    items: [
      "Catégories d'âge automatiques",
      'Classification fédérale par joueur',
      'Export XLSX participants compatible',
      'Restriction par catégorie sur inscription',
    ],
  },
];

const CLUB_FEATURES: FeatureDef[] = [
  {
    icon: Building,
    title: 'Profil public du club',
    items: [
      'Logo, adresse, contact et description',
      'Géolocalisation sur la carte nationale',
      'Fiche annuaire visible par tous les joueurs',
      'Carte interactive (LeafletMap) géolocalisée',
    ],
  },
  {
    icon: UserCheck,
    title: "Demandes d'adhésion",
    badge: 'Workflow',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    items: [
      'Demande en ligne par les joueurs (formulaire)',
      "Validation ou refus par l'admin du club",
      'Notification automatique du résultat',
      'Historique complet des demandes traitées',
    ],
  },
  {
    icon: Users2,
    title: 'Gestion des membres',
    items: [
      'Annuaire complet des licenciés',
      'Numéros de licence et coordonnées',
      'Informations licence renseignées par les utilisateurs (numéro, classification)',
      'Rôles : Membre / Admin club',
    ],
  },
  {
    icon: Trophy,
    title: 'Équipes du club',
    items: [
      'Création et gestion des équipes',
      'Inscriptions groupées aux concours',
      "Suivi des statuts d'inscription",
      'Historique des participations',
    ],
  },
  {
    icon: FileText,
    title: 'Historique & exports',
    items: [
      'Toutes les compétitions passées',
      'Export CSV inscriptions et classements',
      'Résultats et classements par saison',
    ],
  },
];

const PLAYER_FEATURES: FeatureDef[] = [
  {
    icon: MapPin,
    title: 'Recherche géolocalisée',
    badge: 'PostGIS',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      'Recherche par proximité (rayon ajustable)',
      'Carte interactive Leaflet',
      'Filtres : type, niveau, sport, prix',
      'Géolocalisation automatique navigateur',
    ],
  },
  {
    icon: UserPlus,
    title: 'Inscription en ligne',
    items: [
      'Formulaire rapide (équipe + joueurs)',
      'Paiement CB sécurisé via Stripe',
      'Protection anti-fraude hCaptcha',
      'Confirmation immédiate par email',
    ],
  },
  {
    icon: Trophy,
    title: 'Podium & résultats',
    badge: 'Public',
    badgeColor: 'bg-amber-100 text-amber-700',
    items: [
      'Classement final animé avec podium',
      'Résultats accessibles sans connexion',
      'Ex-æquos et rangs spéciaux affichés',
      'Partage sur les réseaux sociaux',
    ],
  },
  {
    icon: LayoutDashboard,
    title: 'Espace personnel',
    items: [
      'Tableau de bord mes inscriptions',
      'Statut de paiement par équipe',
      'Profil joueur + numéro de licence',
      'Statistiques joueur (partenaires, historique)',
    ],
  },
  {
    icon: Bell,
    title: 'Notifications & préférences',
    items: [
      'Confirmation paiement par email',
      'Profil compatible FFPJP (catégorie, classification)',
      'Connexion OAuth Google',
      'Authentification 2 facteurs (MFA)',
    ],
  },
];

export const SPOTLIGHTS = [
  {
    icon: MapPin,
    gradient: 'from-blue-500 to-indigo-600',
    glow: 'shadow-blue-500/30',
    tag: 'Technologie',
    title: 'Géolocalisation PostGIS',
    desc: 'Trouvez les concours dans un rayon de 5 à 300 km grâce à une recherche spatiale haute-performance. Ajustez le rayon, visualisez sur carte Leaflet.',
  },
  {
    icon: CreditCard,
    gradient: 'from-violet-500 to-purple-600',
    glow: 'shadow-violet-500/30',
    tag: 'Paiements',
    title: 'Stripe Connect',
    desc: 'Les organisateurs encaissent directement sur leur compte bancaire. BoulesConnect prélève sa commission automatiquement — sans friction pour le joueur.',
  },
  {
    icon: ClipboardList,
    gradient: 'from-amber-400 to-orange-500',
    glow: 'shadow-amber-500/30',
    tag: 'Résultats',
    title: 'Podium en un clic',
    desc: 'Saisissez le classement final après votre concours. Publication immédiate du podium public avec support des ex-æquos — demi-finalistes, quarts de finale.',
  },
];

export const ROLE_CONFIG: Record<
  RoleId,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    emoji: string;
    color: string;
    activeTab: string;
    activeBg: string;
    sectionBg: string;
    accentBorder: string;
    features: FeatureDef[];
    cta: string;
    ctaHref: string;
  }
> = {
  organizer: {
    label: 'Organisateur',
    icon: Building2,
    emoji: '🏟️',
    color: 'text-blue-600',
    activeTab: 'bg-blue-600 text-white shadow-lg shadow-blue-200',
    activeBg: 'bg-blue-600',
    sectionBg: 'bg-white',
    accentBorder: 'border-blue-200',
    features: ORGANIZER_FEATURES,
    cta: 'Créer un concours gratuitement',
    ctaHref: ROUTES.REGISTER,
  },
  club: {
    label: 'Club',
    icon: Users,
    emoji: '🏆',
    color: 'text-emerald-600',
    activeTab: 'bg-emerald-600 text-white shadow-lg shadow-emerald-200',
    activeBg: 'bg-emerald-600',
    sectionBg: 'bg-emerald-50/40',
    accentBorder: 'border-emerald-200',
    features: CLUB_FEATURES,
    cta: 'Inscrire mon club',
    ctaHref: ROUTES.REGISTER,
  },
  player: {
    label: 'Joueur',
    icon: User,
    emoji: '🎯',
    color: 'text-orange-500',
    activeTab: 'bg-orange-500 text-white shadow-lg shadow-orange-200',
    activeBg: 'bg-orange-500',
    sectionBg: 'bg-white',
    accentBorder: 'border-orange-200',
    features: PLAYER_FEATURES,
    cta: 'Trouver un concours',
    ctaHref: ROUTES.SEARCH_CONCOURS,
  },
};

export const STATS = [
  { value: '3', label: 'profils métier' },
  { value: '50+', label: 'fonctionnalités' },
  { value: 'dès 0,75 €', label: 'commission / inscription' },
  { value: '0 €', label: 'fixe organisateur' },
];
